/**
 * Modal de configuração para node "Pular" (GoTo)
 * Lista todos os nodes do fluxo para seleção como destino
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { FlowNode, GotoConfig } from '@/types/flows';
import { toast } from 'sonner';
import { HelpCircle, CornerDownRight, AlertTriangle, Check, ChevronsUpDown } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { cn } from '@/lib/utils';

interface ConfigureGotoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: GotoConfig) => void;
  allNodes: FlowNode[]; // Todos os nodes do fluxo para seleção
}

/**
 * Dialog de configuração do node Pular
 */
export function ConfigureGotoDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave, 
  allNodes 
}: ConfigureGotoDialogProps) {
  const [targetNodeId, setTargetNodeId] = useState(node?.data.config?.target_node_id || '');
  const [showHelp, setShowHelp] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  /**
   * Sincroniza estado quando node mudar
   */
  useEffect(() => {
    if (node) {
      setTargetNodeId(node.data.config?.target_node_id || '');
    }
  }, [node]);

  /**
   * Lista de nodes que podem ser destino do pulo
   * Exclui: o próprio node goto, start, end, e outros goto
   */
  const targetableNodes = useMemo(() => {
    return allNodes.filter(n => 
      n.id !== node?.id && 
      !['start', 'end', 'goto'].includes(n.data.type)
    );
  }, [allNodes, node?.id]);

  /**
   * Retorna label amigável para o tipo de node
   */
  const getNodeTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'message_text': 'Texto',
      'message_media': 'Mídia',
      'question_buttons': 'Botões',
      'question_list': 'Lista',
      'input_data': 'Coletar Dado',
      'condition': 'Condição',
      'delay': 'Aguardar',
      'add_tag': 'Adicionar Tag',
      'update_contact': 'Atualizar Contato',
      'transfer_human': 'Transferir Humano',
      'transfer_department': 'Transferir Depto',
      'set_variable': 'Definir Variável',
      'webhook': 'Webhook',
    };
    return labels[type] || type;
  };

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    if (!targetNodeId) {
      toast.error('Selecione um node destino');
      return;
    }

    // Busca o node selecionado para pegar o label
    const targetNode = allNodes.find(n => n.id === targetNodeId);
    const targetLabel = targetNode?.data.label || getNodeTypeLabel(targetNode?.data.type || '');

    const config: GotoConfig = {
      target_node_id: targetNodeId,
      target_node_label: targetLabel,
    };

    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as GotoConfig);
    toast.success('Configuração salva com sucesso');
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <CornerDownRight className="h-5 w-5 text-violet-500" />
                Configurar Pulo
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campo ID do Node (somente leitura) */}
            <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

            {/* Seleção do node destino com pesquisa */}
            <div className="space-y-2">
              <Label>Node Destino *</Label>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={popoverOpen}
                    className="w-full justify-between"
                  >
                    {targetNodeId ? (
                      <div className="flex items-center gap-2 truncate">
                        <code className="text-xs bg-muted px-1 rounded">{targetNodeId}</code>
                        <span className="text-muted-foreground text-xs">
                          ({getNodeTypeLabel(targetableNodes.find(n => n.id === targetNodeId)?.data.type || '')})
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Selecione para qual node pular</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                
                <PopoverContent className="w-[450px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar node por ID ou tipo..." />
                    <CommandList>
                      <CommandEmpty>Nenhum node encontrado.</CommandEmpty>
                      <CommandGroup heading="Nodes Disponíveis">
                        {targetableNodes.map((n) => (
                          <CommandItem
                            key={n.id}
                            value={`${n.id} ${getNodeTypeLabel(n.data.type)}`}
                            onSelect={() => {
                              setTargetNodeId(n.id);
                              setPopoverOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                targetNodeId === n.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-muted px-1 rounded">{n.id}</code>
                              <span className="text-muted-foreground text-xs">
                                ({getNodeTypeLabel(n.data.type)})
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                O fluxo irá pular diretamente para este node, ignorando conexões
              </p>
            </div>

            {/* Aviso sobre uso */}
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-yellow-700 dark:text-yellow-400 space-y-1">
                <p><strong>Atenção:</strong> O node "Pular" não precisa de conexão de saída.</p>
                <p>Ele irá diretamente para o node destino selecionado, independente das linhas de conexão no canvas.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!targetNodeId}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="goto" 
      />
    </>
  );
}
